import React, { useContext, useEffect, useState } from "react";
import {
	DefaultErrorMessage,
	FormKeys,
	GetOneSaleActivityRequest,
	Integrations,
	ZomentumEntities
} from "@/Contracts";
import {
	AddMeetingRequest,
	ESalesActivityType,
	MeetingClashStatus,
	MeetingFormValues,
	MeetingTimeDateRange,
	SalesActivity,
	SalesActivityAttendee,
	SalesActivityReminder,
	SalesActivityReminderType,
	SalesActivityReminderUnit,
	SalesActivityUserType
} from "@/Contracts/Others/SalesActivity";
import SalesActivityActionCreators from "../../../../store/actions/SalesActivity";
import {
	SalesActivityModuleState,
	useSalesActivityModuleDispatch,
	useSalesActivityModuleSelector
} from "../../../../store";
import ZMessage from "@molecules/ZMessage";
import { IAddSalesActivityDrawerProps } from "../interface";
import Form from "antd/lib/form";
import ZMeetingDrawer from "../../../../templates/ZDrawers/ZSalesActivities/ZMeeting";
import SalesActivityAddWrapper from "../Wrappers/AddWrapper";
import SalesActivityEditWrapper from "../Wrappers/EditWrapper";
import {
	IMeetingReminders,
	IMeetingUIProps,
	IOnlyMeetingUIProps
} from "../../../../templates/ZDrawers/ZSalesActivities/interface";
import moment from "moment";
import { MissingClientContactType } from "@/Contracts/UI";
import useDatePattern from "@/Hooks/useDatePattern";
import { ParentContext } from "../../../../store/context/ParentContext";
import { UserCompany } from "@/Contracts";
import ZModalConfirm from "@atoms/ZModalConfirmation";
import {
	isPartnerApp,
	isVendorApp
} from "@/Utils";

const Meeting: React.FC<IAddSalesActivityDrawerProps> = ({
	visible = false,
	isEditMode = false,
	onClose,
	onSubmit
}) => {
	const parentContext = useContext(ParentContext);
	const [form] = Form.useForm();
	const [reminders, setReminders] = useState<IMeetingReminders[]>([
		{
			number: 15,
			selection: SalesActivityReminderUnit.Minute
		}
	]);
	const [isFetchingData, setIsFetchingData] = useState<boolean>(false);
	const [meetingClashStatus, setMeetingScheduledStatus] =
		useState<MeetingClashStatus | null>(null);
	const [currentAuthorId, setCurrentAuthorId] = useState<string>("");
	const [attendeesWithoutEmail, setAttendeesWithoutEmail] = useState<
		Array<MissingClientContactType>
	>([]);
	const [showAddMissingDetailsModal, setShowAddMissingDetailsModal] =
		useState<boolean>(false);

	const datePattern = useDatePattern();

	const { isLoading, currentActiveSalesActivity } =
		useSalesActivityModuleSelector((state: SalesActivityModuleState) => {
			return {
				isLoading: state.salesActivity.isLoading,
				currentActiveSalesActivity:
					state.salesActivity.currentActiveSalesActivity
			};
		});
	const {
		isLoading: isUserLoading,
		userData,
		companyUserList,
		userCompany
	} = parentContext.user;

	const { isFormEdited } = parentContext.setting;

	const growUserCompany = { ...userCompany } as UserCompany;
	const growIntegrations: Integrations = { ...growUserCompany.integrations };
	const { currentActivePartner } = parentContext.vendorPartner;

	const dispatch = useSalesActivityModuleDispatch();

	const parseMeetingAttendees = (
		attendees?: SalesActivityAttendee[] | string[],
		parseJson?: boolean
	) => {
		return attendees?.map((rawAttendee: SalesActivityAttendee | string) => {
			const attendee: SalesActivityAttendee = parseJson
				? JSON.parse(rawAttendee as string)
				: rawAttendee;
			if (attendee.attendee_type === SalesActivityUserType.Unknown) {
				return JSON.stringify(
					new SalesActivityAttendee(
						attendee.attendee_id,
						attendee.attendee_type,
						attendee.email
					)
				);
			}
			return JSON.stringify(
				new SalesActivityAttendee(
					attendee.attendee_id,
					attendee.attendee_type
				)
			);
		});
	};

	const getCurrentActiveMeeting = async () => {
		if (currentActiveSalesActivity) {
			setIsFetchingData(true);

			try {
				const getOneSalesActivityRequest =
					new GetOneSaleActivityRequest(
						currentActiveSalesActivity.id,
						currentActiveSalesActivity.feed_type
					);

				await dispatch(
					SalesActivityActionCreators.getOneMeeting(
						getOneSalesActivityRequest
					)
				);
			} catch (error) {
				console.error(error);
			} finally {
				setIsFetchingData(false);
			}
		}
	};

	useEffect(() => {
		if (visible) {
			if (
				isEditMode &&
				currentActiveSalesActivity &&
				currentActiveSalesActivity.feed_details
			) {
				const feed = currentActiveSalesActivity.feed_details;
				const initialCount = feed.all_reminder_details?.length;
				if (initialCount > 0) {
					const initialReminders: IMeetingReminders[] = [];
					feed.all_reminder_details?.forEach((reminder, index) => {
						initialReminders.push({
							number:
								reminder?.reminder_interval ?? 15 * (index + 1),
							selection:
								reminder?.sales_activity_reminder_unit ??
								SalesActivityReminderUnit.Minute
						});
					});
					setReminders(initialReminders);
				}
				setCurrentAuthorId(feed.author_id);

				form.setFieldsValue({
					title: feed.meeting_title,
					description: feed.description,
					start_date: moment(feed.date_time),
					start_time: moment(feed.date_time),
					end_date: moment(feed.end_date_time),
					end_time: moment(feed.end_date_time),
					notes: feed.notes,
					relation: feed.entity_type,
					attendees: feed && parseMeetingAttendees(feed.attendees),
					author_id: feed.author_id,
					location: feed.location,
					reminder_enabled: initialCount > 0
				});
			}
		}
		if (!currentActiveSalesActivity) {
			form.resetFields();
			setReminders([
				{
					number: 15,
					selection: SalesActivityReminderUnit.Minute
				}
			]);
		}
	}, [visible, currentActiveSalesActivity]);

	useEffect(() => {
		if (visible) {
			getCurrentActiveMeeting();

			const { start_date, start_time, end_time, end_date } =
				getMeetingDateTimes();

			fetchMeetingClashStatus(
				{ start_date, start_time, end_date, end_time },
				currentActiveSalesActivity?.id ?? "",
				true
			);
		}
	}, [visible]);

	useEffect(() => {
		if (visible && userData) setCurrentAuthorId(userData.id);
	}, [visible, userData]);

	const handleClose = () => {
		form.resetFields();
		setReminders([
			{
				number: 15,
				selection: SalesActivityReminderUnit.Minute
			}
		]);
		onClose && onClose();
		parentContext.setting.resetFormEdited?.(FormKeys.AddEditMeeting);
	};

	const closeMeetingDrawer = () => {
		if (!isFormEdited?.[FormKeys.AddEditMeeting]) {
			handleClose();
		} else {
			ZModalConfirm({
				content: `Are you sure you want to discard ${
					isEditMode ? "editing" : "adding"
				} meeting?`,
				onOk: handleClose
			});
		}
	};

	const getFormValues = (addMeetingFormValues: MeetingFormValues) => {
		const addMeetingRequest = new AddMeetingRequest();
		addMeetingRequest.title = addMeetingFormValues.title;
		addMeetingRequest.meeting_title = addMeetingFormValues.title;
		addMeetingRequest.description = addMeetingFormValues.description;
		const dateTime = new Date();
		dateTime.setDate(1);
		dateTime.setFullYear(addMeetingFormValues.start_date.year());
		dateTime.setMonth(addMeetingFormValues.start_date.month());
		dateTime.setDate(addMeetingFormValues.start_date.date());
		dateTime.setHours(addMeetingFormValues.start_time.hours());
		dateTime.setMinutes(addMeetingFormValues.start_time.minutes());
		dateTime.setSeconds(0);
		dateTime.setMilliseconds(0);
		addMeetingRequest.date_time = dateTime;
		const endDateTime = new Date();
		endDateTime.setDate(1);
		endDateTime.setFullYear(addMeetingFormValues.end_date.year());
		endDateTime.setMonth(addMeetingFormValues.end_date.month());
		endDateTime.setDate(addMeetingFormValues.end_date.date());
		endDateTime.setHours(addMeetingFormValues.end_time.hours());
		endDateTime.setMinutes(addMeetingFormValues.end_time.minutes());
		endDateTime.setSeconds(0);
		endDateTime.setMilliseconds(0);
		addMeetingRequest.end_date_time = endDateTime;
		addMeetingRequest.author_id =
			addMeetingFormValues.author_id ?? userData?.id;
		addMeetingRequest.entity_type = addMeetingFormValues.relation;
		addMeetingRequest.notes = addMeetingFormValues.notes;
		addMeetingRequest.is_all_day = false;
		if (addMeetingFormValues.relation === ZomentumEntities.Opportunity) {
			addMeetingRequest.entity_id = addMeetingFormValues.opportunity;
		} else if (addMeetingFormValues.relation === ZomentumEntities.Client) {
			addMeetingRequest.entity_id = addMeetingFormValues.client_company;
		} else if (
			addMeetingFormValues.relation === ZomentumEntities.VendorClient
		) {
			addMeetingRequest.entity_id =
				addMeetingFormValues.vendor_client_company;
		} else if (
			addMeetingFormValues.relation === ZomentumEntities.VendorCompany
		) {
			addMeetingRequest.entity_id = addMeetingFormValues.company;
		} else if (
			addMeetingFormValues.relation === ZomentumEntities.VendorOpportunity
		) {
			addMeetingRequest.entity_id =
				addMeetingFormValues.vendor_opportunity;
		}
		addMeetingRequest.all_reminder_details =
			new Array<SalesActivityReminder>();
		if (addMeetingFormValues.reminder_enabled && reminders.length > 0) {
			Object.values(addMeetingFormValues.reminders).forEach(reminder => {
				addMeetingRequest.all_reminder_details.push({
					reminder_type: SalesActivityReminderType.Email,
					reminder_interval: Number(reminder.number),
					sales_activity_reminder_unit: reminder.selection
				});
			});
		}
		addMeetingRequest.attendees = new Array<SalesActivityAttendee>();
		if (
			addMeetingFormValues.attendees &&
			addMeetingFormValues.attendees.length > 0
		) {
			addMeetingFormValues.attendees.forEach(attendeeJSONString => {
				if (attendeeJSONString.includes("attendee_id")) {
					const attendee: SalesActivityAttendee = JSON.parse(
						attendeeJSONString
					) as SalesActivityAttendee;
					addMeetingRequest.attendees.push(attendee);
				} else {
					addMeetingRequest.attendees.push({
						attendee_id: null,
						attendee_type: SalesActivityUserType.Unknown,
						email: attendeeJSONString
					});
				}
			});
		}
		addMeetingRequest.location = addMeetingFormValues.location;

		return addMeetingRequest;
	};

	const addMeeting = async (addMeetingFormValues: MeetingFormValues) => {
		dispatch(SalesActivityActionCreators.showSalesActivityLoader());
		try {
			const addMeetingRequest = getFormValues(addMeetingFormValues);
			if (
				addMeetingRequest.date_time &&
				addMeetingRequest.end_date_time &&
				addMeetingRequest.date_time.getTime() >
					addMeetingRequest.end_date_time.getTime()
			) {
				ZMessage.error("Start time cannot be greater than end time");
			} else {
				const addMeetingResponse = await dispatch(
					SalesActivityActionCreators.addMeeting(addMeetingRequest)
				);
				if (onSubmit) {
					onSubmit(addMeetingResponse.addMeeting);
				}
				// eslint-disable-next-line no-empty
					if (isPartnerApp()) {
				}
				handleClose();
				ZMessage.success("Meeting added successfully");
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(SalesActivityActionCreators.hideSalesActivityLoader());
		}
	};

	const editMeeting = async (editMeetingFormValues: MeetingFormValues) => {
		dispatch(SalesActivityActionCreators.showSalesActivityLoader());
		try {
			if (
				currentActiveSalesActivity &&
				currentActiveSalesActivity.feed_details
			) {
				const editMeetingRequest: SalesActivity = {
					...currentActiveSalesActivity
				};
				const formValues = getFormValues(editMeetingFormValues);

				editMeetingRequest.feed_details = {
					...currentActiveSalesActivity.feed_details,
					...formValues
				};

				if (
					editMeetingRequest.feed_details.date_time &&
					editMeetingRequest.feed_details.end_date_time &&
					editMeetingRequest.feed_details.date_time.getTime() >
						editMeetingRequest.feed_details.end_date_time.getTime()
				) {
					ZMessage.error(
						"Start time cannot be greater than end time"
					);
				} else {
					const editMeetingResponse = await dispatch(
						SalesActivityActionCreators.editMeeting(
							editMeetingRequest
						)
					);

					if (onSubmit) {
						onSubmit(
							editMeetingResponse.editMeeting,
							form.getFieldValue(ZomentumEntities.Opportunity)
						);
					}
					handleClose();
					ZMessage.success("Meeting edited successfully");
				}
			} else {
				ZMessage.error(DefaultErrorMessage);
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(SalesActivityActionCreators.hideSalesActivityLoader());
		}
	};

	const getMeetingDateTimes = (): MeetingTimeDateRange => {
		return form.getFieldsValue([
			"start_time",
			"start_date",
			"end_time",
			"end_date"
		]) as MeetingTimeDateRange;
	};

	const handleOwnerChange = (value: string) => {
		const prevOwner = currentAuthorId;
		setCurrentAuthorId(value);
		const hasValidValue =
			form.getFieldValue("attendees") &&
			Array.isArray(form.getFieldValue("attendees"));
		if (!hasValidValue) {
			form.setFieldsValue({
				attendees: [
					JSON.stringify(
						new SalesActivityAttendee(
							value,
							isVendorApp()
								? SalesActivityUserType.VendorUser
								: SalesActivityUserType.User
						)
					)
				]
			});
			return;
		}

		const newAttendees = (form.getFieldValue("attendees") as string[])
			.map(attendeeJSONString => {
				if (JSON.parse(attendeeJSONString).attendee_id !== prevOwner) {
					if (JSON.parse(attendeeJSONString).attendee_id === value) {
						return "";
					} else {
						return attendeeJSONString;
					}
				}
				return "";
			})
			.filter(str => str);
		newAttendees.push(
			JSON.stringify(
				new SalesActivityAttendee(
					value,
					isVendorApp()
						? SalesActivityUserType.VendorUser
						: SalesActivityUserType.User
				)
			)
		);
		form.setFieldsValue({ attendees: newAttendees });
	};

	const handleAttendeesChange = (value: string[]) => {
		const initialLength = value.length;
		const newAttendees = value.filter((email: string) => {
			if (
				email.includes("attendee_id") &&
				email.includes("attendee_type")
			) {
				return email;
			} else {
				return email.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/);
			}
		});
		if (initialLength !== newAttendees.length) {
			ZMessage.error("Please enter a valid email");
		}
		return newAttendees;
	};

	const fetchMeetingClashStatus = async (
		timeRangePayload: MeetingTimeDateRange,
		id: string,
		isFirstTime?: boolean
	) => {
		if (!visible) {
			return;
		}
		const { start_date, start_time, end_time, end_date } = timeRangePayload;

		try {
			setIsFetchingData(true);
			if (!(start_time && start_date && end_date && end_time)) return;

			const dateTime = new Date();
			dateTime.setDate(1);
			dateTime.setFullYear(start_date.year());
			dateTime.setMonth(start_date.month());
			dateTime.setDate(start_date.date());
			dateTime.setHours(start_time.hours());
			dateTime.setMinutes(start_time.minutes());
			dateTime.setSeconds(0);
			dateTime.setMilliseconds(0);

			const endDateTime = new Date();
			endDateTime.setDate(1);
			endDateTime.setFullYear(end_date.year());
			endDateTime.setMonth(end_date.month());
			endDateTime.setDate(end_date.date());
			endDateTime.setHours(end_time.hours());
			endDateTime.setMinutes(end_time.minutes());
			endDateTime.setSeconds(0);
			endDateTime.setMilliseconds(0);

			const response = await dispatch(
				SalesActivityActionCreators.fetchMeetingClashStatus(
					dateTime.valueOf(),
					endDateTime.valueOf(),
					id ?? ""
				)
			);
			setMeetingScheduledStatus(response.meetingStatus);
		} catch (error) {
			console.error(error);
		} finally {
			setIsFetchingData(false);
			if (!isFirstTime) {
				form.setFieldsValue({
					start_time: null,
					end_time: null,
					start_date: null,
					end_date: null
				});
				setTimeout(
					() =>
						form.setFieldsValue({
							start_time,
							end_time,
							start_date,
							end_date
						}),
					0
				);
			}
		}
	};

	const addReminder = () => {
		const newReminderCount = reminders.length;
		setReminders([
			...reminders,
			{
				number: (newReminderCount + 1) * 15,
				selection: SalesActivityReminderUnit.Minute
			}
		]);
	};

	const deleteReminder = () => {
		setReminders([...reminders].slice(0, -1));
	};

	const showClashingMeetingMessage =
		!isFetchingData &&
		!!meetingClashStatus &&
		meetingClashStatus === MeetingClashStatus.Clashing;

	const handleFormChange = () => {
		!isFormEdited?.[FormKeys.AddEditMeeting] &&
			parentContext.setting.setFormEdited?.(FormKeys.AddEditMeeting);
	};

	const addMeetingUIProps: IOnlyMeetingUIProps = {
		onSubmit: isEditMode ? editMeeting : addMeeting,
		userId:
			userData && userData.id && userData.id.length > 0
				? userData.id
				: null,
		fetchMeetingClashStatus,
		isFetchingData,
		showClashingMeetingMessage,
		handleOwnerChange,
		handleAttendeesChange,
		isAttendeesLoading: isLoading || isUserLoading,
		attendees:
			isEditMode &&
			currentActiveSalesActivity &&
			currentActiveSalesActivity.feed_details
				? currentActiveSalesActivity.feed_details.attendees
				: [],
		companyUserList: companyUserList as any,
		addReminder,
		deleteReminder,
		reminders,
		attendeesWithoutEmail,
		showAddMissingDetailsModal,
		setAttendeesWithoutEmail,
		setShowAddMissingDetailsModal,
		meetingId: currentActiveSalesActivity?.id ?? "",
		partnerContacts: currentActivePartner?.contacts ?? [],
		dateFormat: datePattern,
		handleFormChange,
		integrations: growIntegrations,
		currentMeeting: currentActiveSalesActivity,
		isEditMode
	};

	return (
		<>
			{isEditMode ? (
				<SalesActivityEditWrapper<IMeetingUIProps, IOnlyMeetingUIProps>
					visible={visible}
					onClose={closeMeetingDrawer}
					Component={ZMeetingDrawer}
					form={form}
					formUIProps={addMeetingUIProps}
					salesActivityType={ESalesActivityType.MEETING}
				/>
			) : (
				<SalesActivityAddWrapper<IMeetingUIProps, IOnlyMeetingUIProps>
					visible={visible}
					onClose={closeMeetingDrawer}
					Component={ZMeetingDrawer}
					form={form}
					formUIProps={addMeetingUIProps}
					salesActivityType={ESalesActivityType.MEETING}
				/>
			)}
		</>
	);
};

export default Meeting;
